import _ from 'lodash';

import initialState, { type FiltersState } from './state';
import { type BodyStyle } from '@/constants/body-styles';
import { type PowerType } from '@/components/discovery-questions-input/utils/update-power-type-state/types';
import { type Lifestyle } from '@/constants/lifestyle-preferences';

const addBodyStyle = (state: FiltersState, bodyStyle: BodyStyle) => {
  return {
    ...state,
    selectedBodyStyles: _.concat(state.selectedBodyStyles, bodyStyle),
  };
};

const addLifestyle = (state: FiltersState, lifestyle: Lifestyle) => {
  return {
    ...state,
    selectedLifestyles: _.concat(state.selectedLifestyles, lifestyle),
  };
};

const addMake = (state: FiltersState, make: string) => {
  return {
    ...state,
    selectedMakes: _.concat(state.selectedMakes, make),
  };
};

const addSafetyFeature = (state: FiltersState, safetyFeature: string) => {
  return {
    ...state,
    selectedSafetyFeatures: _.concat(state.selectedSafetyFeatures, safetyFeature),
  };
};

const addStarRating = (state: FiltersState, starRating: string) => {
  return {
    ...state,
    selectedStarRatings: _.concat(state.selectedStarRatings, starRating),
  };
};

const addPowerType = (state: FiltersState, powerType: PowerType) => {
  return {
    ...state,
    selectedPowerTypes: _.concat(state.selectedPowerTypes, powerType),
  };
};

const removeLifestyle = (state: FiltersState, lifestyle: Lifestyle) => {
  return {
    ...state,
    selectedLifestyles: _.filter(state.selectedLifestyles, (item) => item !== lifestyle),
  };
};

const removeSafetyFeature = (state: FiltersState, safetyFeature: string) => {
  return {
    ...state,
    selectedSafetyFeatures: _.filter(state.selectedSafetyFeatures, (item) => item !== safetyFeature),
  };
};

const removeStarRating = (state: FiltersState, starRating: string) => {
  return {
    ...state,
    selectedStarRatings: _.filter(state.selectedStarRatings, (item) => item !== starRating),
  };
};

const removeBodyStyle = (state: FiltersState, bodyStyle: BodyStyle) => {
  return {
    ...state,
    selectedBodyStyles: _.filter(state.selectedBodyStyles, (item) => item !== bodyStyle),
  };
};

const removeMake = (state: FiltersState, make: string) => {
  return {
    ...state,
    selectedMakes: _.filter(state.selectedMakes, (item) => item !== make),
  };
};

const removePowerType = (state: FiltersState, powerType: PowerType) => {
  return {
    ...state,
    selectedPowerTypes: _.filter(state.selectedPowerTypes, (item) => item !== powerType),
  };
};

const updateLifestyles = (state: FiltersState, selectedLifestyles: Lifestyle[]) => {
  return {
    ...state,
    selectedLifestyles,
  };
};

const updateMakes = (state: FiltersState, selectedMakes: string[]) => {
  return {
    ...state,
    selectedMakes,
  };
};

const updateSafetyFeatures = (state: FiltersState, selectedSafetyFeatures: string[]) => {
  return {
    ...state,
    selectedSafetyFeatures,
  };
};

const updateSelectedBodyStyles = (state: FiltersState, selectedBodyStyles: BodyStyle[]) => {
  return {
    ...state,
    selectedBodyStyles,
  };
};

const updateSelectedPriceRange = (state: FiltersState, selectedPriceRange: [number, number]) => {
  return {
    ...state,
    selectedPriceRange,
  };
};

const updateUiSelectedPriceRange = (state: FiltersState, selectedPriceRange: [number, number]) => {
  return {
    ...state,
    uiSelectedPriceRange: selectedPriceRange,
  };
};

const updateSelectedYearRange = (state: FiltersState, selectedYearRange: [number, number]) => {
  return {
    ...state,
    selectedYearRange,
  };
};

const updateUiSelectedYearRange = (state: FiltersState, selectedYearRange: [number, number]) => {
  return {
    ...state,
    uiSelectedYearRange: selectedYearRange,
  };
};

const updateStarRatings = (state: FiltersState, selectedStarRatings: string[]) => {
  return {
    ...state,
    selectedStarRatings,
  };
};

const updatePowerTypes = (state: FiltersState, selectedPowerTypes: PowerType[]) => {
  return {
    ...state,
    selectedPowerTypes,
  };
};

const reset = () => _.cloneDeep(initialState);

const resetSelectedYearRange = (state: FiltersState) => {
  return {
    ...state,
    selectedYearRange: initialState.selectedYearRange,
    uiSelectedYearRange: initialState.selectedYearRange,
  };
};

const resetSelectedPriceRange = (state: FiltersState) => {
  return {
    ...state,
    selectedPriceRange: initialState.selectedPriceRange,
    uiSelectedPriceRange: initialState.selectedPriceRange,
  };
};

const reducers = {
  addBodyStyle,
  addLifestyle,
  addMake,
  addSafetyFeature,
  addStarRating,
  addPowerType,
  removeBodyStyle,
  removeLifestyle,
  removeMake,
  removeSafetyFeature,
  removeStarRating,
  removePowerType,
  reset,
  resetSelectedPriceRange,
  resetSelectedYearRange,
  updateLifestyles,
  updateMakes,
  updateSafetyFeatures,
  updateSelectedBodyStyles,
  updateSelectedPriceRange,
  updateSelectedYearRange,
  updateStarRatings,
  updatePowerTypes,
  updateUiSelectedPriceRange,
  updateUiSelectedYearRange,
};

export type FilterActions = Partial<typeof reducers>;
export default reducers;
